import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { organisationByIdSelector } from '@futrli/shared-services';
import { useIntegrationsView } from '@futrli/views/hooks/useIntegrationsView';

import { StoreState } from '../../core/types';

/**
 * Hook to get an organisation from Redux and its matching integration by explicit IDs.
 *
 * Looks up the org in the normalised Redux store using the provided workspaceId
 * and organisationId, then matches the org's datasource.integration_id to the
 * full Integration object.
 *
 * Note: The Redux org's datasource is a DatasourceListingItem (lightweight),
 * not the full DataSource from the org details API.
 */
export const useOrganisationIntegrationByIds = (workspaceId: string, organisationId: string) => {
  const organisation = useSelector((store: StoreState) =>
    organisationByIdSelector(store.normalised, workspaceId, organisationId)
  );

  const integrations = useIntegrationsView(false).integrations ?? null;

  const datasource = organisation?.datasource;

  const integration = useMemo(
    () => integrations?.find(i => i.id === datasource?.integration_id),
    [integrations, datasource?.integration_id]
  );

  return {
    workspaceId,
    organisationId,
    organisation,
    datasource,
    integration,
    integrations
  };
};
